import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import DeviceHubOutlinedIcon from '@mui/icons-material/DeviceHubOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import type { SvgIconComponent } from '@mui/icons-material';

export type ComparisonQuestion = { q: string; why: string };

export type Comparison = {
  slug: string;
  vendor: string;
  icon: SvgIconComponent;
  /** Short, non-disparaging, generally-known market position — not a claim about their internals. */
  category: string;
  summary: string;
  /**
   * Pricing shape as generally known/marketed (per-endpoint vs. per-technician vs. flat) — not a
   * specific rate, which changes and which we don't have standing to quote. Always paired with a
   * "verify directly" caveat, same discipline as the rest of the site's vendor-neutral content.
   */
  pricingShape: string;
  /**
   * Buyer's-guide-style questions, same technique as BuyersGuide.tsx: ask, don't assert. We don't
   * have verified inside knowledge of a competitor's architecture, so every "what to ask them" item
   * is phrased as a question a prospect can put to that vendor directly, not a claim about the
   * answer. Keeps this honest and keeps us off unverifiable competitive claims.
   */
  whatToAskThem: ComparisonQuestion[];
  /** Nexus facts already established elsewhere on the site (Security.tsx, features.ts, blog) — no new claims introduced here. */
  whatNexusDoesDifferently: string[];
};

export const comparisons: Comparison[] = [
  {
    slug: 'ninjaone',
    vendor: 'NinjaOne',
    icon: DeviceHubOutlinedIcon,
    category: 'Endpoint-scale RMM',
    summary:
      'NinjaOne is one of the largest, most established RMM platforms MSPs run today, known for a broad native feature set and endpoint scale. It grew up RMM-first, with PSA and ticketing capability added over time rather than being the original core.',
    pricingShape:
      'Generally marketed on a per-endpoint pricing model. Always confirm current tiers and minimums directly with NinjaOne — we\'re not in a position to quote a vendor\'s live pricing, and it changes.',
    whatToAskThem: [
      {
        q: 'Is ticketing/PSA the same data model as the RMM, or a separate product connected by an integration?',
        why: 'A platform that grew RMM-first sometimes bolts PSA on afterward — ask whether a ticket and a device share one record or two reconciled ones.',
      },
      {
        q: 'Does the monitoring agent require any inbound access at any client site?',
        why: 'Ask this of every RMM vendor, not just this one — the answer determines what you have to justify at your next client security review.',
      },
      {
        q: 'Is tenant data isolated at the database, or only by application-level filtering?',
        why: 'A vendor operating at scale should have a specific, confident answer, not a process reassurance.',
      },
    ],
    whatNexusDoesDifferently: [
      'PSA and RMM are the same tenant-isolated record from the start, not a ticketing layer connected to monitoring by an integration — an alert becomes a ticket because it is one, not because a sync job created it.',
      'The on-prem monitoring agent is outbound-only — it never listens for anything, so there is no inbound firewall rule to add or justify at any client site.',
      'Every remote job (script, patch, remote-control request) is Ed25519-signed and verified on the endpoint before it runs.',
    ],
  },
  {
    slug: 'syncro',
    vendor: 'Syncro',
    icon: HubOutlinedIcon,
    category: 'Unified PSA + RMM, per-technician',
    summary:
      'Syncro is built PSA-and-RMM-together from the start rather than as two merged products, with native payment processing and per-technician pricing aimed at small-to-mid MSPs — the closest existing platform to Nexus\'s "one platform, one bill" positioning. Nexus\'s own PSA-migration integration is built specifically for MSPs moving off Syncro on their own timeline (see the integration page).',
    pricingShape:
      'Generally marketed on a per-technician pricing model. Confirm current tiers directly with Syncro — pricing and included seats change, and we don\'t have standing to quote them.',
    whatToAskThem: [
      {
        q: 'What does the compliance/posture-tracking module cover beyond a static checklist?',
        why: 'A checked box isn\'t evidence — ask whether a control\'s status reflects live posture and links to actual evidence, or is a manual checklist someone has to remember to update.',
      },
      {
        q: 'Is the monitoring agent outbound-only at every client site?',
        why: 'Worth asking any RMM vendor directly — the answer is either a specific yes or it isn\'t.',
      },
      {
        q: 'How is a remote script or patch job authorized on the way to the endpoint?',
        why: '"It came from our console" and "it\'s cryptographically signed and verified on-device" are different guarantees — ask which one you\'re getting.',
      },
    ],
    whatNexusDoesDifferently: [
      'A three-state (implemented/partial/planned), evidence-linked compliance model across SOC 2, NIST CSF, HIPAA, PCI-DSS, ISO 27001, CIS Controls, GLBA, and FERPA/COPPA — not a static checklist.',
      'Database-enforced tenant isolation via PostgreSQL row-level security, on top of application-level filtering, so an isolation guarantee doesn\'t rest entirely on every future query getting it right.',
      'An agentic AI layer that drafts a diagnosis, resolution, or reply on every module — a human always approves before anything sends, executes, or closes.',
    ],
  },
  {
    slug: 'atera',
    vendor: 'Atera',
    icon: BoltOutlinedIcon,
    category: 'Per-technician all-in-one, AI-marketed',
    summary:
      'Atera markets an all-in-one RMM, PSA, and remote-access bundle on per-technician pricing with unlimited endpoints, and has leaned heavily into AI-branded features. It targets a similar "one platform instead of a stitched stack" pitch to Nexus\'s, from a different starting architecture and a materially larger, already-shipped customer base.',
    pricingShape:
      'Generally marketed on a per-technician, unlimited-endpoint pricing model. Confirm current tiers directly with Atera — we don\'t have standing to quote a competitor\'s live pricing.',
    whatToAskThem: [
      {
        q: 'When an AI feature takes an action, does it execute autonomously or does a human approve first?',
        why: '"AI-powered" is not a specification — ask this of every vendor marketing AI, including us, and get a specific answer about what "AI" actually means for that feature.',
      },
      {
        q: 'Are integration secrets and credentials encrypted at rest, and can vendor staff ever read one back?',
        why: 'Almost every vendor will say "yes, encrypted." The sharper question is whether the reveal path exists at all for anyone on the vendor\'s side.',
      },
      {
        q: 'Unlimited endpoints on per-technician pricing — what happens to support responsiveness as a tenant\'s device count scales up?',
        why: 'A pricing model that decouples cost from device count is worth understanding operationally, not just financially.',
      },
    ],
    whatNexusDoesDifferently: [
      'Every AI-agent draft and every human approval are logged as two separate, linked audit events — not one combined "AI resolved it" entry — specifically so accountability for a consequential action stays traceable to the person who approved it.',
      'Every integration credential is replace-only: it can be rotated but never read back through the UI, support impersonation, or an export, by anyone including us.',
      'Ticket closure specifically stays a human action even when a fix "obviously worked" — an agent can diagnose, draft, and execute a low-risk remediation under policy, but closing is a judgment call, not a status check.',
    ],
  },
  {
    slug: 'connectwise',
    vendor: 'ConnectWise',
    icon: AccountTreeOutlinedIcon,
    category: 'Enterprise PSA + RMM suite',
    summary:
      'ConnectWise is one of the longest-established names in the MSP tooling space, built around ConnectWise Manage (PSA) and ConnectWise Automate/RMM as historically distinct products commonly run together rather than a single ground-up platform — plus a wider suite of adjacent tools (security, quoting, and more) sold as an ecosystem.',
    pricingShape:
      'Generally marketed on a per-endpoint/per-seat basis across separate product lines, often bundled. Confirm current tiers, minimums, and contract terms directly with ConnectWise — a multi-product suite\'s real cost is easy to underestimate from list pricing alone.',
    whatToAskThem: [
      {
        q: 'If I turned off the unified dashboard tomorrow, would the PSA and the RMM still know anything about each other?',
        why: 'This is the direct test for whether two products share one database or are connected by an integration layer that could fail silently — worth asking about any "suite," not just this one.',
      },
      {
        q: 'How many separate logins, contracts, and support channels does a typical deployment actually involve?',
        why: 'A multi-product suite\'s real operational overhead is often higher than the marketing page for any single product implies.',
      },
      {
        q: 'Is tenant data isolated at the database, or only by application-level filtering, across each product in the suite?',
        why: 'Worth asking per-product in a multi-product suite, since different products may have been built by different teams at different times.',
      },
    ],
    whatNexusDoesDifferently: [
      'One tenant-isolated record for ticketing, monitoring, security, and compliance from the start — not several historically separate products reconciled through an integration layer.',
      'One login, one bill, one vendor relationship — the platform pages list what each module includes today so you can see exactly what "one platform" covers before assuming parity with a multi-product suite.',
      'A publicly published, automatically-computed test-to-production ratio on our own README, rather than an "enterprise-grade" claim with no attached number.',
    ],
  },
];

export function getComparisonBySlug(slug: string | undefined): Comparison | undefined {
  return comparisons.find((c) => c.slug === slug);
}
